import { Icon20Search } from '@vkontakte/icons';
import classNames from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { SUPERAPP_WEBSEARCH_ROOT } from 'reactApp/constants';
import { usePortal } from 'reactApp/hooks/usePortal';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { type EStorageType, EActions } from 'reactApp/modules/storage/storage.types';
import ButtonWithSelect from 'reactApp/ui/ButtonWithSelect/ButtonWithSelect';
import { toolbarDropdownItems, toolbarItems } from 'reactApp/ui/Toolbar/Toolbar.data';
import { useAddActions } from 'reactApp/ui/Toolbar/useAddActions';
import { useWebSearchProps } from 'reactApp/ui/WebSearch/helpers';
import { type CustomHandle, WebSearch } from 'reactApp/ui/WebSearch/WebSearch';
import { createGaSender } from 'reactApp/utils/ga';

import styles from './SuperAppToolbar.css';

type ActionToolbarPops = {
    disabled: boolean;
    disableSearch?: boolean;
};

const sendGa = createGaSender('toolbar');
const actionItemMap = {
    [EActions.uploadFile]: {
        text: 'Файл',
    },
    [EActions.uploadFolder]: {
        text: 'Папку',
    },
};

export const SuperAppToolbar = ({ disabled, disableSearch }: ActionToolbarPops) => {
    const placeholderItem = toolbarDropdownItems[EActions.createDoc];
    const storage = useSelector(getCurrentStorage) as EStorageType;

    const origActions = useAddActions({ storage });
    const searchPortalTarget = usePortal(SUPERAPP_WEBSEARCH_ROOT);
    const webSearchProps = useWebSearchProps();
    const [isSearchActive, setIsSearchActive] = useState(false);
    const webSearchRef = useRef<CustomHandle>(null);
    const isSearchAvailable = !disableSearch;

    const handleSearchBtnClicked = useCallback(() => {
        setIsSearchActive(true);
    }, []);

    const handleSearchCloseBtnClicked = useCallback(() => {
        setIsSearchActive(false);
        webSearchRef.current?.closeSearch();
    }, []);

    useEffect(() => {
        if (isSearchActive) {
            webSearchRef.current?.focus();
        }
    }, [isSearchActive]);

    if (origActions.length === 0) {
        origActions.push({ ...toolbarItems.create, list: [placeholderItem] });
        disabled = true;
    }

    const actions = [...origActions].reverse().map((action) => {
        const list = (action?.list ?? []).map((item) => ({ ...item, ...actionItemMap[item.id] }));
        if (list.length) {
            list.push(list.shift());
        } else if (action.id === toolbarItems.create.id) {
            list.push(placeholderItem);
            disabled = true;
        }

        return { ...action, list };
    });

    const flatActions = actions.flatMap(({ list }) => list);
    // Если не доступна опция создания документа, то берем первую доступную
    const buttonValue = flatActions.find(({ id }) => id === EActions.createDoc) || flatActions[0];

    return (
        <div data-qa-id="superAppToolbar" className={styles.root}>
            {isSearchAvailable && (
                <div className={classNames(styles.resetSearch, { [styles.isSearchActive]: isSearchActive })}>
                    <button className={styles.resetSearchButton} onClick={handleSearchCloseBtnClicked} data-qa-id="search-close-button">
                        Сбросить поиск
                    </button>
                </div>
            )}

            <div className={classNames(styles.searchToolbar, { [styles.isSearchActive]: isSearchActive })}>
                <ButtonWithSelect buttonValue={buttonValue.id} disabled={disabled} sendGa={sendGa}>
                    {actions.flatMap((action) => [
                        action.list.length > 0 ? <ButtonWithSelect.Title key={action.id}>{action.text}</ButtonWithSelect.Title> : <div />,

                        ...action.list.map((item) => (
                            <ButtonWithSelect.Option
                                key={item.id}
                                id={item.id}
                                actionTitle={`${action.text} ${item.text?.toLowerCase() ?? ''}`}
                                icon={item.icon}
                                sendGa={sendGa}
                                onClick={item.onClick}
                            >
                                {' '}
                                {item.text}
                            </ButtonWithSelect.Option>
                        )),
                    ])}
                </ButtonWithSelect>

                {isSearchAvailable && (
                    <button className={styles.searchButton} onClick={handleSearchBtnClicked} data-qa-id="search-button">
                        <Icon20Search />
                    </button>
                )}
            </div>
            {isSearchAvailable &&
                createPortal(
                    <div className={classNames(styles.searchWrapper, { [styles.isSearchActive]: isSearchActive })}>
                        <div className={styles.searchContent}>
                            <WebSearch {...webSearchProps} isExpanded noClose ref={webSearchRef} />
                        </div>
                    </div>,
                    searchPortalTarget
                )}
        </div>
    );
};
